import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Card, CopyButton, ErrorNote, Eyebrow, Field, Spinner } from '../../components/ui'
import { api, wsPath } from '../../lib/api'
import { fmtMoney, rewardText } from '../../lib/format'
import { type Locale, useT } from '../../lib/i18n'
import { useRole, useWs } from './useWs'

type Reward = { event: 'sale' | 'lead'; type: 'percent' | 'fixed'; bps?: number; amountMinor?: number }
export type Discount =
  | { type: 'percent'; bps: number; months?: number | null }
  | { type: 'fixed'; amountMinor: number; months?: number | null }
interface Question {
  id: string
  label: string
  required: boolean
}
export interface GroupRow {
  id: string
  name: string
  slug: string
  version: number
  partners: number
  discount: Discount | null
  lander: { headline?: string | null; body?: string | null } | null
  questions: Question[]
  terms: { rewards?: Reward[] | null; holdDays?: number | null }
}
interface ProgramInfo {
  program: { slug: string; currency: string }
  terms: { rewards: Reward[]; holdDays: number }
}

const HOLDS = [0, 7, 14, 30, 60, 90]

export function useGroups(ws: string) {
  return useQuery({
    queryKey: ['groups', ws],
    queryFn: () => api<{ groups: GroupRow[]; defaultPartners: number }>(wsPath(ws, '/groups')),
  })
}

interface Form {
  name: string
  slug: string
  ownReward: boolean
  rewardType: 'percent' | 'fixed'
  rewardValue: string
  holdDays: string
  discountType: '' | 'percent' | 'fixed'
  discountValue: string
  discountMonths: string
  headline: string
  body: string
  questions: Question[]
}

const empty: Form = {
  name: '',
  slug: '',
  ownReward: true,
  rewardType: 'percent',
  rewardValue: '',
  holdDays: '',
  discountType: '',
  discountValue: '',
  discountMonths: '',
  headline: '',
  body: '',
  questions: [],
}

function formOf(g: GroupRow): Form {
  const r = g.terms.rewards?.find((x) => x.event === 'sale') ?? g.terms.rewards?.[0]
  return {
    name: g.name,
    slug: g.slug,
    ownReward: !!r,
    rewardType: r?.type ?? 'percent',
    rewardValue: r ? (r.type === 'percent' ? String(r.bps! / 100) : String(r.amountMinor! / 100)) : '',
    holdDays: g.terms.holdDays == null ? '' : String(g.terms.holdDays),
    discountType: g.discount?.type ?? '',
    discountValue: g.discount
      ? g.discount.type === 'percent'
        ? String(g.discount.bps / 100)
        : String(g.discount.amountMinor / 100)
      : '',
    discountMonths: g.discount?.months ? String(g.discount.months) : '',
    headline: g.lander?.headline ?? '',
    body: g.lander?.body ?? '',
    questions: g.questions,
  }
}

function payload(f: Form, event: 'sale' | 'lead') {
  const v = Number(f.rewardValue)
  const rewards: Reward[] | null =
    f.ownReward && v > 0
      ? [
          f.rewardType === 'percent'
            ? { event, type: 'percent', bps: Math.round(v * 100) }
            : { event, type: 'fixed', amountMinor: Math.round(v * 100) },
        ]
      : null
  const dv = Number(f.discountValue)
  const months = f.discountMonths ? Number(f.discountMonths) : null
  const discount: Discount | null =
    f.discountType && dv > 0
      ? f.discountType === 'percent'
        ? { type: 'percent', bps: Math.round(dv * 100), months }
        : { type: 'fixed', amountMinor: Math.round(dv * 100), months }
      : null
  return {
    name: f.name,
    rewards,
    holdDays: f.holdDays === '' ? null : Number(f.holdDays),
    discount,
    lander: f.headline || f.body ? { headline: f.headline || null, body: f.body || null } : null,
    questions: f.questions.filter((q) => q.label.trim()),
  }
}

export function discountText(d: Discount, currency: string, locale: Locale) {
  const v = d.type === 'percent' ? `${d.bps / 100}%` : fmtMoney(d.amountMinor, currency, locale)
  return d.months ? `${v} × ${d.months}` : v
}

export function GroupsTab() {
  const { t, locale } = useT()
  const ws = useWs()
  const role = useRole()
  const qc = useQueryClient()
  const q = useGroups(ws)
  const prog = useQuery({
    queryKey: ['program', ws],
    queryFn: () => api<ProgramInfo>(wsPath(ws, '/program')),
  })
  const [editing, setEditing] = useState<string | null>(null) // group id, 'new' or null
  const [f, setF] = useState<Form>(empty)
  const editable = role === 'owner' || role === 'admin'
  const inv = () => qc.invalidateQueries({ queryKey: ['groups', ws] })
  const save = useMutation({
    mutationFn: () => {
      const event = prog.data?.terms.rewards[0]?.event ?? 'sale'
      return editing === 'new'
        ? api(wsPath(ws, '/groups'), { body: { ...payload(f, event), slug: f.slug } })
        : api(wsPath(ws, `/groups/${editing}`), { method: 'PATCH', body: payload(f, event) })
    },
    onSuccess: () => {
      setEditing(null)
      inv()
    },
  })
  const archive = useMutation({
    mutationFn: (id: string) => api(wsPath(ws, `/groups/${id}`), { method: 'DELETE' }),
    onSuccess: () => {
      setEditing(null)
      inv()
      qc.invalidateQueries({ queryKey: ['partners', ws] })
    },
  })
  if (q.isLoading || prog.isLoading) return <Spinner />
  if (!q.data || !prog.data) return <ErrorNote error={q.error ?? prog.error} />
  const { currency, slug } = prog.data.program
  const base = prog.data.terms
  const joinUrl = (g?: string) => `${location.origin}/join/${slug}${g ? `?group=${g}` : ''}`

  const termsLine = (rewards: Reward[] | null | undefined, hold: number | null | undefined) =>
    `${(rewards?.length ? rewards : base.rewards).map((r) => rewardText(r, currency, locale)).join(' + ')} · ${t('holdDays')} ${hold ?? base.holdDays} ${t('days')}`

  return (
    <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
      <div className="grid content-start gap-4">
        <p className="text-sm text-body">{t('groupsHint')}</p>
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <Eyebrow className="mb-2">{t('defaultGroup')}</Eyebrow>
              <div className="text-body">{termsLine(null, null)}</div>
              <div className="mt-1 text-sm text-mute">
                {t('partnersCount', { n: String(q.data.defaultPartners) })}
              </div>
            </div>
            <CopyButton value={joinUrl()} />
          </div>
        </Card>
        {q.data.groups.map((g) => (
          <Card key={g.id}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <Eyebrow className="mb-2">
                  {g.name} <span className="mono opacity-60">· v{g.version}</span>
                </Eyebrow>
                <div className="text-body">{termsLine(g.terms.rewards, g.terms.holdDays)}</div>
                {g.discount && (
                  <div className="mt-1 text-sm text-body">
                    {t('customerDiscount')}: {discountText(g.discount, currency, locale)}
                  </div>
                )}
                <div className="mt-1 text-sm text-mute">
                  {t('partnersCount', { n: String(g.partners) })}
                  {g.questions.length ? ` · ${t('questionsCount', { n: String(g.questions.length) })}` : ''}
                </div>
              </div>
              <div className="flex gap-2">
                <CopyButton value={joinUrl(g.slug)} />
                {editable && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setEditing(g.id)
                      setF(formOf(g))
                    }}
                  >
                    {t('edit')}
                  </button>
                )}
              </div>
            </div>
          </Card>
        ))}
        {editable && editing === null && (
          <button
            type="button"
            className="btn btn-primary justify-self-start"
            onClick={() => {
              setEditing('new')
              setF(empty)
            }}
          >
            {t('addGroup')}
          </button>
        )}
      </div>

      {editing !== null && (
        <Card>
          <Eyebrow className="mb-4">{editing === 'new' ? t('addGroup') : t('editGroup')}</Eyebrow>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              save.mutate()
            }}
          >
            <Field label={t('groupName')}>
              <input
                className="input"
                required
                maxLength={80}
                value={f.name}
                onChange={(e) => setF({ ...f, name: e.target.value })}
              />
            </Field>
            {editing === 'new' && (
              <Field label={t('groupSlug')}>
                <input
                  className="input mono"
                  required
                  pattern="[a-z0-9][a-z0-9\-]{1,31}"
                  value={f.slug}
                  onChange={(e) => setF({ ...f, slug: e.target.value.toLowerCase() })}
                  placeholder="vip"
                />
              </Field>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={f.ownReward}
                onChange={(e) => setF({ ...f, ownReward: e.target.checked })}
              />
              {t('ownReward')}
            </label>
            {f.ownReward && (
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('rewardType')}>
                  <select
                    className="select"
                    value={f.rewardType}
                    onChange={(e) => setF({ ...f, rewardType: e.target.value as Form['rewardType'] })}
                  >
                    <option value="percent">%</option>
                    <option value="fixed">{currency}</option>
                  </select>
                </Field>
                <Field label={t('rewardValue')}>
                  <input
                    className="input"
                    type="number"
                    required
                    min={0.01}
                    max={f.rewardType === 'percent' ? 100 : undefined}
                    step={0.01}
                    value={f.rewardValue}
                    onChange={(e) => setF({ ...f, rewardValue: e.target.value })}
                  />
                </Field>
              </div>
            )}
            <Field label={t('holdDays')}>
              <select
                className="select"
                value={f.holdDays}
                onChange={(e) => setF({ ...f, holdDays: e.target.value })}
              >
                <option value="">
                  {t('asProgram')} ({base.holdDays})
                </option>
                {HOLDS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label={t('customerDiscount')}>
                <select
                  className="select"
                  value={f.discountType}
                  onChange={(e) => setF({ ...f, discountType: e.target.value as Form['discountType'] })}
                >
                  <option value="">—</option>
                  <option value="percent">%</option>
                  <option value="fixed">{currency}</option>
                </select>
              </Field>
              <Field label={t('rewardValue')}>
                <input
                  className="input"
                  type="number"
                  min={0.01}
                  step={0.01}
                  disabled={!f.discountType}
                  value={f.discountValue}
                  onChange={(e) => setF({ ...f, discountValue: e.target.value })}
                />
              </Field>
              <Field label={t('months')}>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={36}
                  disabled={!f.discountType}
                  value={f.discountMonths}
                  onChange={(e) => setF({ ...f, discountMonths: e.target.value })}
                />
              </Field>
            </div>
            <p className="-mt-2 text-xs text-mute">{t('discountHint')}</p>
            <Field label={t('landerHeadline')}>
              <input
                className="input"
                maxLength={120}
                value={f.headline}
                onChange={(e) => setF({ ...f, headline: e.target.value })}
              />
            </Field>
            <Field label={t('landerBody')}>
              <textarea
                className="textarea"
                rows={3}
                maxLength={4000}
                value={f.body}
                onChange={(e) => setF({ ...f, body: e.target.value })}
              />
            </Field>
            <div>
              <Eyebrow className="mb-2">{t('applicationQuestions')}</Eyebrow>
              <div className="grid gap-2">
                {f.questions.map((qq, i) => (
                  <div key={qq.id} className="flex items-center gap-2">
                    <input
                      className="input"
                      maxLength={200}
                      value={qq.label}
                      aria-label={t('question')}
                      onChange={(e) =>
                        setF({
                          ...f,
                          questions: f.questions.map((x, j) =>
                            j === i ? { ...x, label: e.target.value } : x,
                          ),
                        })
                      }
                    />
                    <label className="flex items-center gap-1 whitespace-nowrap text-xs">
                      <input
                        type="checkbox"
                        checked={qq.required}
                        onChange={(e) =>
                          setF({
                            ...f,
                            questions: f.questions.map((x, j) =>
                              j === i ? { ...x, required: e.target.checked } : x,
                            ),
                          })
                        }
                      />
                      {t('required')}
                    </label>
                    <button
                      type="button"
                      className="btn"
                      aria-label={t('remove')}
                      onClick={() => setF({ ...f, questions: f.questions.filter((_, j) => j !== i) })}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {f.questions.length < 5 && (
                  <button
                    type="button"
                    className="btn justify-self-start"
                    onClick={() =>
                      setF({
                        ...f,
                        questions: [
                          ...f.questions,
                          { id: `q${Date.now().toString(36)}`, label: '', required: false },
                        ],
                      })
                    }
                  >
                    + {t('question')}
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="submit" className="btn btn-primary" disabled={save.isPending}>
                {t('save')}
              </button>
              <button type="button" className="btn" onClick={() => setEditing(null)}>
                {t('cancel')}
              </button>
              {editing !== 'new' && (
                <button
                  type="button"
                  className="btn btn-danger ml-auto"
                  disabled={archive.isPending}
                  onClick={() => {
                    if (confirm(t('archiveGroupConfirm'))) archive.mutate(editing)
                  }}
                >
                  {t('archive')}
                </button>
              )}
            </div>
            <ErrorNote error={save.error ?? archive.error} />
          </form>
        </Card>
      )}
    </div>
  )
}
